import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { courseDetails, courseFaqs, courseComparisonData } from '@/mocks/courseDetailData';

export default function Courses() {
  const [searchParams] = useSearchParams();
  const activeCourseId = searchParams.get('course') || courseDetails[1].id;
  const [selectedTab, setSelectedTab] = useState(activeCourseId);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const activeCourse = courseDetails.find((c) => c.id === selectedTab) || courseDetails[0];

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative pt-28 pb-20 px-4 md:px-6 bg-background-100 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-primary-500 blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-accent-500 blur-3xl"></div>
          </div>
          <div className="mx-auto max-w-7xl relative">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-xs text-foreground-500 tracking-widest uppercase mb-4">课程体系</p>
              <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6">
                系统化AI学习路径，从<span className="text-primary-500">入门到精通</span>
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                三阶课程体系，覆盖AI领域最核心的技术方向。无论你的起点如何，都能找到最适合的学习路径。
              </p>
            </div>
          </div>
        </section>

        {/* Course Tabs */}
        <section className="py-12 px-4 md:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {courseDetails.map((course) => (
                <button
                  key={course.id}
                  onClick={() => setSelectedTab(course.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedTab === course.id
                      ? 'bg-foreground-900 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {course.title}
                </button>
              ))}
            </div>

            {/* Course Detail */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <div className="relative aspect-[16/10] rounded-2xl overflow-hidden">
                <img
                  src={activeCourse.image}
                  alt={activeCourse.title}
                  className="w-full h-full object-cover object-top"
                  title={`${activeCourse.title} - bbgpt.online`}
                />
                <div className="absolute top-4 left-4">
                  <span className="inline-block px-3 py-1 bg-accent-500 text-foreground-950 text-xs font-semibold rounded-full whitespace-nowrap">
                    {activeCourse.level}
                  </span>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">{activeCourse.title}</h2>
                <p className="text-sm text-foreground-500 mb-6 leading-relaxed">{activeCourse.tagline}</p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{activeCourse.description}</p>

                <div className="flex flex-wrap items-center gap-5 mb-6">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-700 font-medium">{activeCourse.rating}</span>
                    <span className="text-xs text-foreground-400">({activeCourse.reviewCount} 评价)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-user-line text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{activeCourse.students.toLocaleString()} 学员</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-time-line text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-600">{activeCourse.duration} / {activeCourse.totalHours}课时</span>
                  </div>
                </div>

                <div className="flex items-baseline gap-2 mb-6">
                  <span className="font-heading text-3xl md:text-4xl text-primary-500">¥{activeCourse.price.toLocaleString()}</span>
                  <span className="text-sm text-foreground-400 line-through">¥{activeCourse.originalPrice.toLocaleString()}</span>
                  <span className="text-xs text-accent-500 font-medium">省 ¥{(activeCourse.originalPrice - activeCourse.price).toLocaleString()}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                  {activeCourse.features.map((f) => (
                    <span key={f} className="text-xs px-2.5 py-1 bg-background-100 text-foreground-600 rounded-full whitespace-nowrap">
                      {f}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/checkout?course=${selectedTab}`}
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap text-sm"
                >
                  立即报名
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Curriculum & Who Is This For */}
        <section className="py-16 md:py-20 bg-background-100 px-4 md:px-6">
          <div className="mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <h3 className="font-heading text-xl text-foreground-950 mb-8">课程大纲</h3>
                <div className="space-y-4">
                  {activeCourse.curriculum.map((week) => (
                    <div key={week.week} className="bg-background-50 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-semibold px-3 py-1 bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                          {week.week}
                        </span>
                        <h4 className="text-sm font-semibold text-foreground-900">{week.title}</h4>
                      </div>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {week.topics.map((topic) => (
                          <li key={topic} className="flex items-center gap-2 text-xs text-foreground-600">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-checkbox-circle-line text-accent-500 text-xs"></i>
                            </div>
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-heading text-xl text-foreground-950 mb-6">适合人群</h3>
                <div className="bg-background-50 rounded-xl p-5 space-y-3">
                  {activeCourse.whoIsThisFor.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-user-star-line text-accent-500 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 bg-foreground-900 rounded-xl p-6">
                  <h4 className="font-heading text-lg text-background-50 mb-3">不确定选哪个？</h4>
                  <p className="text-xs text-background-300 leading-relaxed mb-4">
                    联系我们的课程顾问，获取一对一学习路径规划建议。
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-xs text-accent-400 hover:text-accent-300 transition-colors cursor-pointer"
                  >
                    咨询课程顾问
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">课程对比</h2>
              <p className="text-sm text-foreground-500">快速了解三套方案的区别，选择最适合你的学习路径</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200">
                    {courseComparisonData.headers.map((h, i) => (
                      <th
                        key={h}
                        className={`py-4 px-4 text-left font-medium whitespace-nowrap ${
                          i === 0 ? 'text-foreground-500 text-xs' : 'text-foreground-900'
                        } ${i === 2 ? 'bg-primary-50/30' : ''}`}
                      >
                        {i === 2 && (
                          <span className="inline-block px-2 py-0.5 bg-accent-500 text-foreground-950 text-xs rounded-full mr-1.5">推荐</span>
                        )}
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {courseComparisonData.rows.map((row, idx) => (
                    <tr key={row.label} className={`border-b border-background-100 ${idx % 2 === 0 ? 'bg-background-50' : ''}`}>
                      <td className="py-3.5 px-4 text-foreground-600 text-xs whitespace-nowrap">{row.label}</td>
                      {row.values.map((v, vi) => (
                        <td
                          key={vi}
                          className={`py-3.5 px-4 whitespace-nowrap ${
                            vi === 1 ? 'bg-primary-50/30 text-foreground-700' : 'text-foreground-600'
                          }`}
                        >
                          {v === '—' ? <span className="text-foreground-300">{v}</span> : v}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-100 px-4 md:px-6">
          <div className="mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">常见问题</h2>
              <p className="text-sm text-foreground-500">关于课程的常见疑问，在这里找到答案</p>
            </div>

            <div className="space-y-3">
              {courseFaqs.map((faq, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${expandedFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-400"></i>
                    </div>
                  </button>
                  {expandedFaq === idx && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 md:py-28 px-4 md:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
              准备好开启AI学习之旅了吗？
            </h2>
            <p className="text-sm text-foreground-500 mb-8">
              超过{5000}+学员已经在bbgpt.online完成了技能提升，下一个就是你
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={`/checkout?course=${selectedTab}`}
                className="px-8 py-3.5 bg-foreground-900 text-background-50 font-medium rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap text-sm"
              >
                立即报名{selectedTab === 'ai-basics' ? '基础班' : selectedTab === 'ml-practical' ? '实战班' : '进阶班'}
              </Link>
              <Link
                to="/contact"
                className="px-8 py-3.5 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-all cursor-pointer whitespace-nowrap text-sm"
              >
                咨询课程顾问
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}